import { ButtonFlavor, ButtonType, ExButton, ExInput } from '@boomi/exosphere';
import { useState } from 'react';
import { addIntegrationAccount, validateIntegrationAccount } from '../../sources/tenant';
import translations from '../../translations';
import Modal from '../generic/modal/GenericModal';
import { useNotificationDispatch } from '../notifications/NotificationProvider';

type AddIntegrationAccountModalProps = {
    show: boolean;
    onAdded: (username: string, accountId: string, apiKey: string) => void;
    onClose: () => void;
};

const AddIntegrationAccountModal = ({
    show,
    onAdded,
    onClose,
}: AddIntegrationAccountModalProps) => {
    const [username, setUsername] = useState('');
    const [accountId, setAccountId] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const dispatchNotification = useNotificationDispatch();

    const handleUsernameChange = (e: Event) => {
        setUsername((e.target as HTMLInputElement).value);
    };

    const handleAccountIdChange = (e: Event) => {
        setAccountId((e.target as HTMLInputElement).value);
    };

    const handleAPIKeyChange = (e: Event) => {
        setApiKey((e.target as HTMLInputElement).value);
    };

    const isAccountIdValid = () => {
        return accountId !== '' && /\w{0,25}-\w{6}/i.test(accountId);
    };

    const handleAdd = async () => {
        setHasSubmitted(true);

        if (isAccountIdValid() && apiKey) {
            try {
                const isValid = await validateIntegrationAccount(username, accountId, apiKey);

                if (!isValid) {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: { error: translations.TENANT_integration_accounts_invalid },
                    });
                    return;
                }

                await addIntegrationAccount(username, accountId, apiKey);
                dispatchNotification({
                    type: 'notifySuccess',
                    payload: { message: translations.TENANT_integration_accounts_added_success },
                });

                onAdded(username, accountId, apiKey);
                setAccountId('');
                setApiKey('');
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            }
        }
    };

    const handleCancel = () => {
        setAccountId('');
        setApiKey('');
        onClose();
    };

    return (
        show && (
            <Modal
                onHide={handleCancel}
                title="Add Integration Account"
                show
                footerClassName="add-integration-account-modal-footer"
                renderBody={() => (
                    <>
                        <ExInput
                            label={translations.TENANT_integration_accounts_username}
                            value={username}
                            onChange={handleUsernameChange}
                            required={true}
                            invalid={hasSubmitted && !username}
                            error-msg={translations.TENANT_integration_accounts_username_required}
                            type="text"
                            id="add-integration-account-username"
                            data-testid="add-integration-account-username"
                        />
                        <ExInput
                            label={translations.TENANT_integration_accounts_account_id}
                            value={accountId}
                            onChange={handleAccountIdChange}
                            required={true}
                            invalid={hasSubmitted && !isAccountIdValid()}
                            error-msg={translations.TENANT_integration_accounts_account_id_required}
                            type="text"
                            id="add-integration-account-id"
                            data-testid="add-integration-account-id"
                        />
                        <ExInput
                            label={translations.TENANT_integration_accounts_api_key}
                            value={apiKey}
                            onChange={handleAPIKeyChange}
                            required={true}
                            invalid={
                                hasSubmitted &&
                                !/^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/.test(apiKey)
                            }
                            error-msg={translations.TENANT_integration_accounts_api_key_required}
                            type="password"
                            togglePassword={true}
                            id="add-integration-account-api-key"
                            data-testid="add-integration-account-api-key"
                        />
                    </>
                )}
                renderFooter={() => (
                    <>
                        <ExButton
                            type={ButtonType.SECONDARY}
                            flavor={ButtonFlavor.BRANDED}
                            onClick={handleCancel}
                        >
                            {translations.COMMON_cancel}
                        </ExButton>
                        <ExButton
                            type={ButtonType.PRIMARY}
                            flavor={ButtonFlavor.BRANDED}
                            onClick={handleAdd}
                        >
                            {translations.COMMON_add}
                        </ExButton>
                    </>
                )}
            />
        )
    );
};

export default AddIntegrationAccountModal;
